import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, ChevronDown, MapPin, Star } from 'lucide-react';
import { format, addDays, startOfWeek, endOfWeek, isToday, isSameDay, addWeeks, subWeeks } from 'date-fns';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';
import { getCalendarBookings, getBooking } from '../lib/bookings';
import { getBookingFeedback } from '../lib/feedback';
import BookingInfoModal from '../components/BookingInfoModal';
import DayHeader from '../components/calendar/DayHeader';
import '../components/MobileListView.css';
import CreateMobileBookingModal from '../components/CreateMobileBookingModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
// Removed all persistence systems
import { usePermissions } from '../contexts/PermissionContext';
import { usePermissionCheck } from '../hooks/usePermissionCheck';
import { sendSMS } from '../lib/messaging';

// Define WorkingHours interfaces
interface WorkingHoursShift {
  start: string;
  end: string;
}

interface DayConfig {
  enabled: boolean;
  shifts: WorkingHoursShift[];
  areas?: string[];
  radius: number;
}

interface WorkingHours {
  [key: string]: DayConfig;
}

// Define ResourceBusinessHour interface
interface ResourceBusinessHour {
  resourceId: string;
  startTime: string;
  endTime: string;
  daysOfWeek: number[];
}

// Helper function to format date and time into ISO string
const formatDateTime = (date: string, time: string): string => {
  return `${date}T${time}`;
};

export default function MobileCalendar() {
  const { selectedBusiness } = useBusiness();
  const { currentResource } = usePermissions();
  const { canAccess } = usePermissionCheck();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingBookingDetails, setIsLoadingBookingDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mobileResources, setMobileResources] = useState<any[]>([]);
  const [resourceBusinessHours, setResourceBusinessHours] = useState<ResourceBusinessHour[]>([]);
  const [mobileAvailability, setMobileAvailability] = useState<any[]>([]);
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [viewStartDate, setViewStartDate] = useState<Date>(startOfWeek(new Date()));
  const [viewEndDate, setViewEndDate] = useState<Date>(endOfWeek(new Date()));
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [viewType, setViewType] = useState<'week' | 'day'>('day');
  const [isViewDropdownOpen, setIsViewDropdownOpen] = useState(false);

  const [isBufferModalOpen, setIsBufferModalOpen] = useState(false);
  const [selectedBufferBlock, setSelectedBufferBlock] = useState<any>(null);
  const [isDeleteBufferModalOpen, setIsDeleteBufferModalOpen] = useState(false);
  const [isBufferBlockModalOpen, setIsBufferBlockModalOpen] = useState(false);
  const [isGoogleMapsModalOpen, setIsGoogleMapsModalOpen] = useState(false);
  const [selectedBookingAddress, setSelectedBookingAddress] = useState<string>('');
  const [selectedBookingForSMS, setSelectedBookingForSMS] = useState<any>(null);
  const [isSendingSMS, setIsSendingSMS] = useState(false);
  const [bookingFeedback, setBookingFeedback] = useState<Map<string, { rating: number; hasText: boolean }>>(new Map());
  
  // Today's summary data state
  const [todaysSales, setTodaysSales] = useState(0);
  const [todaysBookings, setTodaysBookings] = useState(0);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  
  // Mobile booking reschedule state
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [rescheduleData, setRescheduleData] = useState<any>(null);
  
  // Quick booking state
  const [isQuickBookingModalOpen, setIsQuickBookingModalOpen] = useState(false);
  const [quickBookingData, setQuickBookingData] = useState<any>(null);
  
  // Handle mobile booking reschedule
  const handleRescheduleMobileBooking = () => {
    if (!selectedBooking || !selectedBooking.mobile_booking) return;

    // Transform booking data into the format expected by CreateMobileBookingModal
    const prefilledData = {
      customer: selectedBooking.customer ? {
        id: selectedBooking.customer.id,
        first_name: selectedBooking.customer.first_name || '',
        last_name: selectedBooking.customer.last_name || '',
        email: selectedBooking.customer.email || null,
        phone: selectedBooking.customer.phone || '',
        business_id: selectedBooking.business_id,
        type: 'person',
        address: selectedBooking.booking_address || null,
        source: null,
        tags: [],
        last_interaction: null,
        lifetime_value: 0,
        next_service_date: null,
        referral_source: null,
        preferences: {},
        created_at: selectedBooking.customer.created_at,
        updated_at: selectedBooking.customer.updated_at
      } : null,
      services: (selectedBooking.services || []).map((service: any) => ({
        vehicle: service.vehicle_id ? {
          id: service.vehicle_id,
          make: service.vehicle_make || '',
          model: service.vehicle_model || '',
          year: service.vehicle_year || '',
          color: service.vehicle_color || '',
          customer_id: selectedBooking.customer?.id || '',
          business_id: selectedBooking.business_id,
          created_at: '',
          updated_at: ''
        } : null,
        service: {
          id: service.service_id,
          name: service.name,
          description: service.description,
          price: service.price,
          duration_minutes: service.duration_minutes,
          business_id: selectedBooking.business_id,
          category: '',
          tax_rate: service.tax_rate,
          tax_exempt: service.tax_exempt,
          active: true,
          price_options: [],
          created_at: '',
          updated_at: ''
        },
        priceOption: {
          id: '',
          service_id: service.service_id,
          name: 'Standard',
          price: service.price,
          duration_minutes: service.duration_minutes
        }
      })),
      scheduleData: {
        dropOffDate: selectedBooking.drop_off_date || selectedBooking.start_date || '',
        dropOffTime: selectedBooking.drop_off_time || selectedBooking.start_time || '',
        startDate: selectedBooking.start_date || '',
        startTime: selectedBooking.start_time || '',
        endDate: selectedBooking.end_date || '',
        endTime: selectedBooking.end_time || '',
        customer_notes: selectedBooking.customer_notes || ''
      },
      bookingAddress: selectedBooking.booking_address || '',
      bookingColor: selectedBooking.color || '#3B82F6',
      bookingSecondaryColor: selectedBooking.secondary_color,
      bookingSource: selectedBooking.source || null
    };

    setRescheduleData(prefilledData);
    setIsRescheduleModalOpen(true);
  };
  
  // Handle quick booking from timeline slot click
  const handleQuickBooking = (resource: any, slotTime: string, availableHours: number, date: Date) => {
    // Format the date and time for the booking
    const formattedDate = format(date, 'yyyy-MM-dd');
    const [hours, minutes] = slotTime.split(':').map(Number);
    
    // Calculate end time based on available hours (default to 1 hour if no specific service selected)
    const endTime = new Date(date);
    endTime.setHours(hours + Math.min(availableHours, 1), minutes, 0, 0);
    
    const quickData = {
      resource: {
        id: resource.id,
        name: resource.title,
        resource_id: resource.id
      },
      scheduleData: {
        startDate: formattedDate,
        startTime: slotTime,
        endDate: format(endTime, 'yyyy-MM-dd'),
        endTime: format(endTime, 'HH:mm'),
        availableHours: availableHours
      },
      isQuickBooking: true
    };

    setQuickBookingData(quickData);
    setIsQuickBookingModalOpen(true);
  };
  
  // Modal persistence hook
  // Removed all persistence systems

  // Get greeting message based on user type
  const getGreetingMessage = () => {
    if (!currentResource) {
      return 'Welcome back, Admin';
    }
    
    if (currentResource.is_admin) {
      return 'Welcome back, Admin';
    }
    
    if (currentResource.first_name) {
      return `Welcome back, ${currentResource.first_name}`;
    }
    
    return 'Welcome back, Admin';
  };

  // Fetch today's summary data (sales and bookings)
  const fetchTodaysSummary = useCallback(async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    setIsLoadingSummary(true);
    try {
      const today = format(currentDate, 'yyyy-MM-dd');
      
      // Check if current user is admin
      const isAdmin = currentResource?.is_admin || false;
      
      let bookingsQuery = supabase
        .from('bookings')
        .select('id, subtotal, discount_amount, status, end_date')
        .eq('business_id', selectedBusiness.id)
        .eq('end_date', today)
        .eq('is_deleted', false)
        .neq('status', 'cancelled');

      // If not admin, filter by current resource's bookings
      if (!isAdmin && currentResource?.id) {
        // Get booking IDs that are assigned to the current resource
        const { data: resourceBookings, error: resourceError } = await supabase
          .from('booking_resources')
          .select('booking_id')
          .eq('resource_id', currentResource.id);

        if (resourceError) {
          console.error('Error fetching resource bookings:', resourceError);
          return;
        }

        const bookingIds = resourceBookings?.map(rb => rb.booking_id) || [];
        
        if (bookingIds.length === 0) {
          // No bookings for this resource today
          setTodaysSales(0);
          setTodaysBookings(0);
          return;
        }

        // Filter bookings by the resource's booking IDs
        bookingsQuery = bookingsQuery.in('id', bookingIds);
      }

      // Fetch today's bookings
      const { data: bookingsData, error: bookingsError } = await bookingsQuery;

      if (bookingsError) {
        console.error('Error fetching today\'s bookings:', bookingsError);
        return;
      }

      // Calculate total sales after discounts
      const totalSales = (bookingsData || []).reduce((sum, booking) => {
        const subtotal = booking.subtotal || 0;
        const discountAmount = booking.discount_amount || 0;
        return sum + (subtotal - discountAmount);
      }, 0);

      setTodaysSales(totalSales);
      setTodaysBookings(bookingsData?.length || 0);
      
    } catch (error) {
      console.error('Error fetching today\'s summary:', error);
    } finally {
      setIsLoadingSummary(false);
    }
  }, [selectedBusiness?.id, currentDate, supabase, currentResource]);

  // Removed all persistence systems
  
  // Initialize week start date and end date
  useEffect(() => {
    if (viewType === 'week') {
      // For week view: rolling 7-day view starting from currentDate
      setViewStartDate(currentDate);
      setViewEndDate(addDays(currentDate, 6)); // Show 7 days starting from currentDate
    } else {
      // For day view: use traditional week boundaries
      setViewStartDate(startOfWeek(currentDate));
      setViewEndDate(endOfWeek(currentDate));
    }
  }, [currentDate, viewType]);
  
  // Update isMobile state on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch today's summary data when date changes
  useEffect(() => {
    fetchTodaysSummary();
  }, [fetchTodaysSummary]);
  
  // Fetch mobile resources and availability
  const fetchMobileResources = useCallback(async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      // First get mobile availability records
      const { data: availabilityData, error: availabilityError } = await supabase
        .from('mobile_availability')
        .select('resource_id, working_hours')
        .eq('business_id', selectedBusiness.id)
        .eq('mobile_available', true);

      if (availabilityError) throw availabilityError;
      
      if (!availabilityData?.length) {
        setMobileResources([]);
        setResourceBusinessHours([]);
        setMobileAvailability([]);
        return;
      }
      
      // Store the availability data
      setMobileAvailability(availabilityData);

      // Get the resource details
      const resourceIds = availabilityData.map(ma => ma.resource_id);
      const { data: resources, error: resourcesError } = await supabase
        .from('resources')
        .select('id, first_name, last_name')
        .in('id', resourceIds);

      if (resourcesError) throw resourcesError;

      // Format resources for display
      const formattedResources = resources?.map(resource => ({
        id: resource.id,
        title: `${resource.first_name} ${resource.last_name}`.trim()
      })) || [];

      // Filter resources based on "View All Bookings" permission
      const filteredResources = canAccess.viewAllBookings 
        ? formattedResources 
        : formattedResources.filter(resource => resource.id === currentResource?.id);

      console.log(`${canAccess.viewAllBookings ? 'Showing all resources' : `Filtered to current resource: ${currentResource?.first_name} ${currentResource?.last_name}`} (${filteredResources.length} resource${filteredResources.length !== 1 ? 's' : ''})`);

      // Parse working hours from mobile_availability
      const businessHours: ResourceBusinessHour[] = [];
      
      // Filter availability data to match the filtered resources
      const filteredAvailabilityData = canAccess.viewAllBookings 
        ? availabilityData 
        : availabilityData.filter(ma => ma.resource_id === currentResource?.id);
      
      // Process for each resource
      filteredAvailabilityData.forEach(ma => {
        try {
          const workingHours = ma.working_hours as WorkingHours;
          
          // For each day of the week
          ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].forEach((day, dayIndex) => {
            const dayConfig = workingHours[day];
            
            if (dayConfig?.enabled && dayConfig.shifts?.length > 0) {
              // Add each shift
              dayConfig.shifts.forEach(shift => {
                businessHours.push({
                  resourceId: ma.resource_id,
                  startTime: shift.start,
                  endTime: shift.end,
                  daysOfWeek: [dayIndex]
                });
              });
            }
          });
        } catch (error) {
          console.error('Error parsing working hours for resource:', ma.resource_id, error);
        }
      });

      setMobileResources(filteredResources);
      setResourceBusinessHours(businessHours);
      setMobileAvailability(filteredAvailabilityData);
    } catch (error) {
      console.error('Error fetching mobile resources:', error);
      setError('Failed to load mobile resources. Please try again.');
    }
  }, [selectedBusiness?.id, canAccess.viewAllBookings, currentResource?.id]);
  
  // Fetch events/bookings for the current week
  const fetchEvents = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      setIsLoading(true);
      
      console.log(`Fetching MOBILE bookings for week: ${viewStartDate.toDateString()} to ${viewEndDate.toDateString()}`);
      
      // Determine if we should filter by resource based on the viewAllBookings permission
      const shouldFilterByResource = !canAccess.viewAllBookings && currentResource?.id;
      const filterResourceId = shouldFilterByResource ? currentResource.id : undefined;
      
      // Fetch only mobile bookings (isLocationBooking = false)
      const events = await getCalendarBookings(
        selectedBusiness.id,
        viewStartDate,
        viewEndDate,
        false, // mobile bookings
        filterResourceId // Pass the resource ID for filtering if needed
      );
      
      console.log(`Fetched ${events.length} mobile bookings${shouldFilterByResource ? ` filtered for resource ${currentResource.first_name} ${currentResource.last_name}` : ' (all bookings)'}`);
      
      // Now also fetch buffer blocks from mobile_bookings table
      if (supabase) {
        const { data: bufferBlocks, error: bufferError } = await supabase
          .from('mobile_bookings')
          .select(`
            id,
            business_id,
            resource_id,
            booking_id,
            start_date,
            start_time,
            end_date,
            end_time,
            buffer_time,
            is_buffer_block
          `)
          .eq('business_id', selectedBusiness.id)
          .eq('is_buffer_block', true)
          .gte('start_date', viewStartDate.toISOString().split('T')[0])
          .lte('end_date', viewEndDate.toISOString().split('T')[0]);
          
        if (bufferError) {
          console.error('Error fetching buffer blocks:', bufferError);
        } else {
          console.log(`Fetched ${bufferBlocks?.length || 0} buffer blocks`);
          
          // Process buffer blocks into CalendarBooking format
          const bufferEvents = (bufferBlocks || []).map(block => {
            // Format dates for display - ensure exact time formatting
            const startDate = new Date(`${block.start_date}T${block.start_time}`);
            const endDate = new Date(`${block.end_date}T${block.end_time}`);
            
            // Make sure there's no duration error by explicitly setting time components
            const start = startDate.toISOString();
            const end = endDate.toISOString();
            
            console.log(`Buffer block time: ${start} to ${end}`);
            
            // Create a buffer block event
            return {
              id: `buffer-${block.id}`, // Prefix to identify as buffer
              title: `Buffer`,
              booking_number: 0, // Buffer blocks don't have booking numbers
              start,
              end,
              backgroundColor: '#000000', // Black for buffer blocks
              borderColor: '#000000',
              secondaryColor: null,
              allDay: false,
              display: 'block',
              resourceId: block.resource_id,
              extendedProps: {
                status: 'buffer',
                is_buffer_block: true,
                buffer_time: block.buffer_time,
                booking_id: block.booking_id,
                resource_id: block.resource_id,
                mobile_booking: true,
                // Minimum required fields to match booking structure
                customer: 'Buffer',
                booking_number: 0,
                total_amount: 0,
                payments: [],
                services: []
              }
            };
          });
          
          // Process events for display (both regular bookings and buffer blocks)
          const processedEvents = [...events, ...bufferEvents].map(event => {
            return {
              ...event,
              display: 'block',
              backgroundColor: event.backgroundColor,
              secondaryColor: event.secondaryColor,
              borderColor: event.backgroundColor
            };
          });
          
          setEvents(processedEvents);
          return;
        }
      }
      
      // If supabase client is not available or if fetching buffer blocks failed
      // Process events without buffer blocks
      const processedEvents = events.map(event => {
        return {
          ...event,
          display: 'block',
          backgroundColor: event.backgroundColor,
          secondaryColor: event.secondaryColor,
          borderColor: event.backgroundColor
        };
      });
      
      setEvents(processedEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
      setError('Failed to load bookings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedBusiness?.id, viewStartDate, viewEndDate, canAccess.viewAllBookings, currentResource?.id]);

  // Add a function to load feedback for completed bookings
  const loadFeedbackForBookings = useCallback(async (bookings: any[]) => {
    if (!selectedBusiness?.id) return;
    
    const completedBookings = bookings.filter(booking => booking.extendedProps?.status === 'completed');
    if (completedBookings.length === 0) return;
    
    const feedbackMap = new Map<string, { rating: number; hasText: boolean }>();
    
    // Load feedback for each completed booking
    await Promise.all(
      completedBookings.map(async (booking) => {
        try {
          const feedback = await getBookingFeedback(booking.id);
          if (feedback) {
            // Calculate average rating from form_responses or legacy fields
            let averageRating = 0;
            let ratingCount = 0;
            
            if (feedback.form_responses && Object.keys(feedback.form_responses).length > 0) {
              // Use dynamic form responses
              Object.values(feedback.form_responses).forEach(value => {
                if (typeof value === 'number' && value > 0) {
                  averageRating += value;
                  ratingCount++;
                }
              });
              if (ratingCount > 0) {
                averageRating = averageRating / ratingCount;
              }
            } else {
              // Fallback to legacy fields
              if (feedback.overall_quality_rating) {
                averageRating += feedback.overall_quality_rating;
                ratingCount++;
              }
              if (feedback.overall_experience_rating) {
                averageRating += feedback.overall_experience_rating;
                ratingCount++;
              }
              if (ratingCount > 0) {
                averageRating = averageRating / ratingCount;
              }
            }
            
            if (averageRating > 0) {
              feedbackMap.set(booking.id, {
                rating: averageRating,
                hasText: !!(feedback.improvement_suggestions || 
                          (feedback.form_responses && Object.values(feedback.form_responses).some(v => typeof v === 'string' && v.trim())))
              });
            }
          }
        } catch (error) {
          console.error('Error loading feedback for booking:', booking.id, error);
        }
      })
    );
    
    setBookingFeedback(feedbackMap);
  }, [selectedBusiness?.id]);
  
  // Load resources and events when component mounts or when dates change
  useEffect(() => {
    fetchMobileResources();
  }, [fetchMobileResources]);
  
  useEffect(() => {
    fetchEvents();
    fetchTodaysSummary(); // Also refresh today's summary when events are fetched
  }, [fetchEvents, fetchTodaysSummary]);

  // Listen for mobile booking creation events to refresh calendar
  useEffect(() => {
    const handleMobileBookingCreated = () => {
      console.log('📱 Mobile booking created event received, refreshing calendar...');
      fetchEvents();
      fetchTodaysSummary(); // Also refresh today's summary
    };

    window.addEventListener('mobileBookingCreated', handleMobileBookingCreated);
    
    return () => {
      window.removeEventListener('mobileBookingCreated', handleMobileBookingCreated);
    };
  }, [fetchEvents, fetchTodaysSummary]);

  // Load feedback when events change
  useEffect(() => {
    if (events.length > 0) {
      loadFeedbackForBookings(events);
    }
  }, [events, loadFeedbackForBookings]);
  
  // Handler for booking click - fetch the full booking details
  const handleBookingClick = async (booking: any) => {
    try {
      setIsLoadingBookingDetails(true);
      
      // Check if this is a buffer block
      if (booking.extendedProps?.is_buffer_block) {
        console.log('Buffer block clicked:', booking);
        
        // Set the selected buffer block and open the modal
        setSelectedBufferBlock({
          id: booking.id,
          buffer_time: booking.extendedProps.buffer_time,
          booking_id: booking.extendedProps.booking_id,
          start: new Date(booking.start),
          end: new Date(booking.end),
          resource_id: booking.extendedProps.resource_id
        });
        setIsBufferModalOpen(true);
        setIsLoadingBookingDetails(false);
        return;
      }
      
      // Fetch full booking details
      const fullBooking = await getBooking(booking.id);
      if (fullBooking) {
        setSelectedBooking(fullBooking);
        setIsBookingModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching booking details:', error);
      setError('Failed to load booking details. Please try again.');
    } finally {
      setIsLoadingBookingDetails(false);
    }
  };
  
  // Navigation handlers
  const handlePrevWeek = () => {
    const newDate = new Date(currentDate);
    if (viewType === 'day') {
      // Day view: move back 1 day
      newDate.setDate(currentDate.getDate() - 1);
    } else {
      // Week view: move back 7 days (rolling 7-day view moves week by week)
      newDate.setDate(currentDate.getDate() - 7);
    }
    setCurrentDate(newDate);
  };
  
  const handleNextWeek = () => {
    const newDate = new Date(currentDate);
    if (viewType === 'day') {
      // Day view: move forward 1 day
      newDate.setDate(currentDate.getDate() + 1);
    } else {
      // Week view: move forward 7 days (rolling 7-day view moves week by week)
      newDate.setDate(currentDate.getDate() + 7);
    }
    setCurrentDate(newDate);
  };
  
  const handleToday = () => {
    setCurrentDate(new Date());
  };
  
  // Helper to format time
  const formatTime = (time: string) => {
    if (!time) return '';
    
    // Format as 12-hour time (h:mm a)
    const [hours, minutes] = time.split(':');
    const parsedHours = parseInt(hours, 10);
    const ampm = parsedHours >= 12 ? 'PM' : 'AM';
    const displayHours = parsedHours % 12 || 12;
    
    return `${displayHours}:${minutes} ${ampm}`;
  };
  
  // Function to handle view change
  const handleViewChange = (view: 'week' | 'day') => {
    setViewType(view);
    setIsViewDropdownOpen(false);
  };
  

  
  // Helper to calculate payment status

  
  // Add the function to handle buffer block deletion
  const handleDeleteBufferBlock = async () => {
    if (!supabase || !selectedBufferBlock || !selectedBufferBlock.id) {
      console.error('Cannot delete buffer block: Missing data or database connection');
      return;
    }
    
    try {
      setIsLoadingBookingDetails(true);
      
      // Extract the actual ID from the buffer-{id} format
      const bufferId = selectedBufferBlock.id.replace('buffer-', '');
      
      // Delete the buffer block from the mobile_bookings table
      const { error } = await supabase
        .from('mobile_bookings')
        .delete()
        .eq('id', bufferId);
      
      if (error) {
        console.error('Error deleting buffer block:', error);
        throw error;
      }
      
      // Close the modal and refresh events
      setIsBufferModalOpen(false);
      fetchEvents(); // Refresh the calendar to show changes
      
      // Show success message
      alert('Buffer block successfully deleted');
    } catch (error) {
      console.error('Failed to delete buffer block:', error);
      alert('Failed to delete buffer block. Please try again.');
    } finally {
      setIsLoadingBookingDetails(false);
    }
  };
  
  // Function to handle opening Google Maps
  const openGoogleMaps = (address: string) => {
    const encodedAddress = encodeURIComponent(address);
    const mapsUrl = `https://maps.google.com/?q=${encodedAddress}`;
    
    // Try to open the Google Maps app if on mobile, otherwise open in browser
    if (isMobile) {
      // Try to open Google Maps app first
      const googleMapsApp = `comgooglemaps://?q=${encodedAddress}`;
      const appleMapsApp = `maps://maps.apple.com/?q=${encodedAddress}`;
      
      // Create a hidden link to try opening the app
      const link = document.createElement('a');
      link.href = googleMapsApp;
      link.style.display = 'none';
      document.body.appendChild(link);
      
      // Try to click the link
      link.click();
      
      // After a short delay, open web version as fallback
      setTimeout(() => {
        window.open(mapsUrl, '_blank');
        document.body.removeChild(link);
      }, 500);
    } else {
      // On desktop, just open in browser
      window.open(mapsUrl, '_blank');
    }
  };

  // Function to handle map pin button click
  const handleMapPinClick = (event: any, e: React.MouseEvent) => {
    e.stopPropagation();
    
    const address = event.extendedProps?.booking_address;
    if (!address) {
      alert('No address available for this booking');
      return;
    }
    
    setSelectedBookingAddress(address);
    setSelectedBookingForSMS(event);
    setIsGoogleMapsModalOpen(true);
  };

  // Function to handle map and notify (opens maps + sends SMS)
  const handleMapAndNotify = async () => {
    if (!selectedBookingForSMS || !selectedBusiness?.id) {
      alert('Missing booking or business information');
      return;
    }

    setIsSendingSMS(true);
    
    try {
      // Get customer information from the booking
      const fullCustomerName = selectedBookingForSMS.extendedProps?.customer || 'there';
      const customerFirstName = fullCustomerName.split(' ')[0]; // Extract only first name
      const customerPhone = selectedBookingForSMS.extendedProps?.customer_phone;
      
      if (!customerPhone) {
        alert('No phone number available for this customer');
        setIsSendingSMS(false);
        return;
      }

      // Create the SMS message
      const message = `Hey ${customerFirstName}, I am on my way`;

      // Send the SMS
      const result = await sendSMS({
        businessId: selectedBusiness.id,
        to: customerPhone,
        message: message,
        aiGenerated: false
      });

      if (result.success) {
        // Open Google Maps
        openGoogleMaps(selectedBookingAddress);
        
        // Close the modal
        setIsGoogleMapsModalOpen(false);
        
        // Show success message
        alert(`SMS sent to ${customerFirstName}!`);
      } else {
        alert(`Failed to send SMS: ${result.error}`);
      }
    } catch (error) {
      console.error('Error sending SMS:', error);
      alert('Failed to send SMS. Please try again.');
    } finally {
      setIsSendingSMS(false);
    }
  };

  // Add helper function to get the service title
  const getServiceTitle = (booking: any) => {
    if (!booking?.extendedProps?.services || booking.extendedProps.services.length === 0) {
      return 'No service';
    }
    
    // Get the first service name
    const firstService = booking.extendedProps.services[0];
    return firstService.name || 'Unnamed service';
  };

  // Helper function to get resource information
  const getResourceInfo = (resourceId: string) => {
    return mobileResources.find(resource => resource.id === resourceId);
  };

  // Helper function to create resource avatar
  const ResourceAvatar = ({ resourceId, size = 'w-6 h-6' }: { resourceId: string; size?: string }) => {
    const resource = getResourceInfo(resourceId);
    
    if (!resource) {
      return (
        <div className={`${size} rounded-full bg-gray-400 flex items-center justify-center flex-shrink-0`}>
          <span className="text-xs font-medium text-white">?</span>
        </div>
      );
    }

    // Create initials from the resource title
    const initials = resource.title.split(' ').map((name: string) => name[0]).join('').substring(0, 2);
    
    return (
      <div className={`${size} rounded-full bg-white/20 flex items-center justify-center flex-shrink-0`}>
        <span className="text-xs font-medium text-white">
          {initials}
        </span>
      </div>
    );
  };
  
  if (error) {
    return (
      <div className="flex flex-col h-full overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen" style={{ 
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-md w-full">
            <div className="bg-white/90 backdrop-blur-xl rounded-2xl border border-red-200/50 shadow-lg p-6" style={{
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)'
            }}>
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Error loading calendar</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed">{error}</p>
                  <button 
                    onClick={() => {
                      setError(null);
                      fetchEvents();
                    }}
                    className="inline-flex items-center px-4 py-2.5 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-2xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
                  >
                    Try again
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Calculate if we have any events this week (for empty state)
  const hasEventsThisWeek = events.length > 0;
  
  // Filter events for the current day (for day view)
  const todaysEvents = events.filter(event => {
    const eventDate = new Date(event.start);
    return (
      eventDate.getDate() === currentDate.getDate() &&
      eventDate.getMonth() === currentDate.getMonth() &&
      eventDate.getFullYear() === currentDate.getFullYear()
    );
  // Sort events by start time to ensure proper chronological order
  }).sort((a, b) => {
    const aStart = new Date(a.start).getTime();
    const bStart = new Date(b.start).getTime();
    return aStart - bStart;
  });
  
  return (
    <div className="flex flex-col h-full overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen" style={{ 
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      paddingTop: 'env(safe-area-inset-top, 0px)',
      paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      paddingLeft: 'env(safe-area-inset-left, 0px)',
      paddingRight: 'env(safe-area-inset-right, 0px)'
    }}>
      {/* Modern header with glassy blur effect - Optimized for mobile week view */}
      <header className={`bg-gray-800 border-b border-gray-700 shadow-lg py-2 sticky top-0 ${viewType === 'week' ? 'px-2 sm:px-4 md:px-8' : 'px-4 md:px-8'}`} style={{
        zIndex: 50
      }}>
        <div className="flex flex-col space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <h1 className="text-xl md:text-2xl font-bold text-white">Mobile Bookings</h1>
            </div>
            
            {/* Modern view selection dropdown */}
            <div className="relative">
              <button 
                className="inline-flex items-center justify-center px-3 py-2 text-sm font-semibold text-white bg-gray-700 border border-gray-600 rounded-xl hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-all duration-200 ease-out touch-manipulation"
                onClick={() => setIsViewDropdownOpen(!isViewDropdownOpen)}
                style={{ minHeight: '36px' }}
              >
                {viewType === 'week' ? 'Week View' : 'Day View'}
                <ChevronDown className={`ml-2 h-4 w-4 transition-transform duration-300 ${isViewDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isViewDropdownOpen && (
                <div className="absolute right-0 mt-2 w-36 rounded-2xl shadow-2xl bg-white/95 backdrop-blur-xl border border-gray-200/50 z-10 transform transition-all duration-300 ease-out animate-in slide-in-from-top-2 fade-in-0" style={{
                  backdropFilter: 'blur(20px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(20px) saturate(180%)'
                }}>
                  <div className="py-2" role="menu">
                    <button 
                      className={`w-full text-left px-4 py-3 text-sm transition-all duration-200 ease-out touch-manipulation ${
                        viewType === 'day' ? 'bg-purple-50 text-purple-900 font-semibold' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                      onClick={() => handleViewChange('day')}
                      style={{ minHeight: '44px' }}
                    >
                      Day View
                    </button>
                    <button 
                      className={`w-full text-left px-4 py-3 text-sm transition-all duration-200 ease-out touch-manipulation ${
                        viewType === 'week' ? 'bg-purple-50 text-purple-900 font-semibold' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                      onClick={() => handleViewChange('week')}
                      style={{ minHeight: '44px' }}
                    >
                      Week View
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <button 
                onClick={handlePrevWeek}
                className="p-2.5 rounded-2xl bg-white/80 backdrop-blur-sm border border-gray-300/50 hover:bg-white/90 text-gray-600 hover:text-gray-800 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
                aria-label={viewType === 'day' ? "Previous Day" : "Previous Week"}
                style={{ minHeight: '44px', minWidth: '44px' }}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              
              <button
                onClick={handleToday}
                className={`px-4 py-2.5 text-sm font-semibold rounded-2xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation ${
                  isToday(currentDate) 
                    ? 'text-white bg-blue-600 hover:bg-blue-700' 
                    : 'text-gray-400 bg-gray-200 hover:bg-gray-300'
                }`}
                style={{ minHeight: '44px' }}
              >
                Today
              </button>
              
              <button
                onClick={handleNextWeek}
                className="p-2.5 rounded-2xl bg-white/80 backdrop-blur-sm border border-gray-300/50 hover:bg-white/90 text-gray-600 hover:text-gray-800 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
                aria-label={viewType === 'day' ? "Next Day" : "Next Week"}
                style={{ minHeight: '44px', minWidth: '44px' }}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
            
            {/* Date range display */}
            <span className="text-sm text-white font-medium">
              {viewType === 'day' 
                ? format(currentDate, 'EEE, MMM d')
                : `${format(viewStartDate, 'MMM d')} - ${format(viewEndDate, 'MMM d, yyyy')}`
              }
            </span>
          </div>
        </div>
      </header>
      
      {/* Main content with modern styling - Optimized for mobile week view */}
      <main className={`flex-1 overflow-auto py-6 pb-20 ${viewType === 'week' ? 'px-2 sm:px-4 md:px-8' : 'px-4 md:px-8'}`}>
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="w-12 h-12 border-3 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto shadow-lg shadow-purple-600/30"></div>
              <p className="mt-4 text-base text-gray-600 font-medium">Loading bookings...</p>
            </div>
          </div>
        ) : viewType === 'day' ? (
          // Modern Day View with glassy design
          <div className="max-w-2xl mx-auto">
            {/* Compact summary card */}
            <div className="mb-3 bg-gradient-to-r from-purple-600 to-indigo-700 rounded-xl p-3 shadow-lg border border-white/20" style={{
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)'
            }}>
              <div className="flex items-center justify-between mb-2">
                <h1 className="text-base font-bold text-white">
                  {getGreetingMessage()}
                </h1>
              </div>
              
              {isLoadingSummary ? (
                <div className="grid grid-cols-2 gap-2">
                  <div className="animate-pulse bg-white/20 rounded-lg h-12"></div>
                  <div className="animate-pulse bg-white/20 rounded-lg h-12"></div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {/* Sales Card */}
                  <div className="bg-white/15 backdrop-blur-sm rounded-lg p-2 border border-white/25 shadow-sm">
                    <div className="text-center">
                      <p className="text-xs text-white/70 font-medium mb-0.5">Today's Sales</p>
                      <p className="text-sm font-bold text-white">
                        ${todaysSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                  
                  {/* Bookings Card */}
                  <div className="bg-white/15 backdrop-blur-sm rounded-lg p-2 border border-white/25 shadow-sm">
                    <div className="text-center">
                      <p className="text-xs text-white/70 font-medium mb-0.5">Total Bookings</p>
                      <p className="text-sm font-bold text-white">
                        {todaysBookings}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Modern bookings list */}
            <div className="space-y-4">
              {todaysEvents.length > 0 ? (
                todaysEvents.map((event, index) => {
                  const status = event.extendedProps.status;
                  const secondaryColor = event.extendedProps.secondaryColor;
                  const isBufferBlock = event.extendedProps?.is_buffer_block === true;
                  
                  // Special styling for buffer blocks with modern design
                  if (isBufferBlock) {
                    return (
                      <div 
                        key={`buffer-${index}`} 
                        className="bg-gray-900/90 backdrop-blur-xl rounded-2xl border border-gray-800/50 shadow-lg hover:shadow-xl transition-all duration-300 ease-out transform hover:scale-[1.02] active:scale-[0.98] cursor-pointer touch-manipulation"
                        onClick={() => handleBookingClick(event)}
                        style={{
                          backdropFilter: 'blur(20px) saturate(180%)',
                          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                          animationDelay: `${index * 50}ms`
                        }}
                      >
                        <div className="p-5">
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0 mr-3">
                              <p className="text-base font-semibold text-white mb-1">
                                Buffer Block
                              </p>
                              <p className="text-sm font-medium text-white/80 mb-2">
                                {format(new Date(event.start), 'h:mm a')} - {format(new Date(event.end), 'h:mm a')}
                              </p>
                              <div className="flex items-center space-x-3">
                                <p className="text-xs text-white/70">
                                  {event.extendedProps.buffer_time} minutes buffer time
                                </p>
                                
                                {/* Resource Avatar for Buffer */}
                                {event.extendedProps.resource_id && (
                                  <ResourceAvatar resourceId={event.extendedProps.resource_id} size="w-8 h-8" />
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  
                  // Modern booking card design
                  const backgroundStyle = status === 'in_progress' ? {
                    background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)', // Modern yellow gradient
                    borderColor: '#f59e0b'
                  } : status === 'cancelled' ? {
                    background: 'linear-gradient(135deg, #fca5a5 0%, #ef4444 100%)', // Modern red gradient
                    borderColor: '#ef4444'
                  } : secondaryColor ? {
                    background: `linear-gradient(135deg, ${event.backgroundColor} 0%, ${secondaryColor} 100%)`
                  } : {
                    background: `linear-gradient(135deg, ${event.backgroundColor} 0%, ${event.backgroundColor} 100%)`
                  };
                  
                  const startTime = new Date(event.start);
                  const endTime = event.end ? new Date(event.end) : new Date(startTime.getTime() + 60 * 60 * 1000);
                  
                  return (
                    <div 
                      key={index} 
                      className="bg-white/90 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-lg hover:shadow-xl transition-all duration-300 ease-out transform hover:scale-[1.02] active:scale-[0.98] cursor-pointer touch-manipulation overflow-hidden"
                      onClick={() => handleBookingClick(event)}
                      style={{
                        backdropFilter: 'blur(20px) saturate(180%)',
                        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                        animationDelay: `${index * 50}ms`
                      }}
                    >
                      {/* Modern booking header with gradient */}
                      <div 
                        className="p-5"
                        style={backgroundStyle}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0 mr-3">
                            <div className="flex items-center space-x-2 mb-2">
                              <p className={`text-base font-semibold truncate ${status === 'cancelled' ? 'text-white' : 'text-white'}`}>
                                #{event.extendedProps.booking_number} {event.title}
                              </p>
                              
                              {/* Feedback Rating for completed bookings */}
                              {status === 'completed' && bookingFeedback.get(event.id) && (
                                <div className="flex items-center bg-white/20 rounded-lg px-2 py-1 flex-shrink-0">
                                  <Star 
                                    size={14} 
                                    className="text-yellow-400 fill-current mr-1" 
                                  />
                                  <span className="text-white text-sm font-semibold">
                                    {bookingFeedback.get(event.id)!.rating.toFixed(1)}
                                  </span>
                                </div>
                              )}
                            </div>
                            
                            <p className={`text-sm font-medium mb-1 ${status === 'cancelled' ? 'text-white/90' : 'text-white/90'}`}>
                              {format(startTime, 'h:mm a')} - {format(endTime, 'h:mm a')}
                            </p>
                            
                            <p className={`text-sm ${status === 'cancelled' ? 'text-white/80' : 'text-white/80'}`}>
                              {event.extendedProps.customer || 'No customer info'}
                            </p>
                          </div>
                          
                          <div className="flex flex-col items-end space-y-2">
                            {/* Payment Status Badge */}
                            {(() => {
                              const totalAmount = event.extendedProps.total_amount || 0;
                              const payments = event.extendedProps.payments || [];
                              const totalPaid = payments
                                .filter((p: any) => p.status === 'completed')
                                .reduce((sum: number, p: any) => sum + p.amount, 0);

                              let paymentStatus = '';
                              
                              if (totalAmount > 0) {
                                if (totalPaid === 0) {
                                  paymentStatus = 'UNPAID';
                                } else if (totalPaid >= totalAmount) {
                                  paymentStatus = 'PAID';
                                } else {
                                  paymentStatus = 'PARTIAL';
                                }
                              }
                              
                              return paymentStatus ? (
                                <div className="px-3 py-1 text-xs font-bold rounded-lg bg-white/20 text-white backdrop-blur-sm">
                                  {paymentStatus}
                                </div>
                              ) : null;
                            })()}
                            
                            <span className={`text-lg font-bold ${status === 'cancelled' ? 'text-white' : 'text-white'}`}>
                              ${event.extendedProps.total_amount?.toFixed(2) || '0.00'}
                            </span>
                          </div>
                        </div>
                        
                        {/* Bottom row with status and actions */}
                        <div className="flex justify-between items-center mt-4">
                          <div className="flex items-center space-x-3">
                            <span className={`text-sm font-medium ${status === 'cancelled' ? 'text-white/90' : 'text-white/90'}`}>
                              {event.extendedProps.status === 'scheduled' && '🕒 Scheduled'}
                              {event.extendedProps.status === 'completed' && '✅ Completed'}
                              {event.extendedProps.status === 'in_progress' && '🔄 In Progress'}
                              {event.extendedProps.status === 'cancelled' && '❌ Cancelled'}
                              {event.extendedProps.status === 'created' && '📝 Created'}
                              {event.extendedProps.mobile_booking && ' • Mobile'}
                            </span>
                            
                            {/* Resource Avatar */}
                            {event.extendedProps.resource_id && (
                              <ResourceAvatar resourceId={event.extendedProps.resource_id} size="w-8 h-8" />
                            )}
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            {/* Modern action buttons */}
                            <button 
                              className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm border border-white/30 text-white hover:bg-white/30 transition-all duration-200 flex items-center justify-center"
                              onClick={(e) => handleMapPinClick(event, e)}
                              title="Open in Google Maps"
                            >
                              <MapPin className="h-4 w-4" />
                            </button>
                            

                          </div>
                        </div>
                      </div>
                      

                    </div>
                  );
                })
              ) : (
                // Modern empty state
                <div className="flex flex-col items-center justify-center py-16 px-6">
                  <div className="bg-gradient-to-br from-purple-50 to-indigo-100 rounded-3xl p-6 mb-6">
                    <CalendarIcon className="h-12 w-12 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No bookings today</h3>
                  <p className="text-gray-600 text-center max-w-md leading-relaxed">
                    There are no mobile bookings scheduled for today. Enjoy your day!
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : !hasEventsThisWeek && mobileResources.length === 0 ? (
          // Modern empty state when no resources or events in week view
          <div className="flex flex-col items-center justify-center py-16 px-6">
            <div className="bg-gradient-to-br from-purple-50 to-indigo-100 rounded-3xl p-6 mb-6">
              <CalendarIcon className="h-12 w-12 text-purple-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No mobile bookings</h3>
            <p className="text-gray-600 text-center max-w-md leading-relaxed">
              There are no mobile resources available or no mobile bookings scheduled for this week.
            </p>
          </div>
        ) : (
          // Modern Week View - Days stacked vertically with resources for each day (Full width optimized)
          <div className="space-y-2 sm:space-y-4 pb-20 w-full">
            {/* Days of the week */}
            {Array.from({ length: 7 }).map((_, dayIndex) => {
              // Calculate the date for this day
              const dayDate = new Date(viewStartDate);
              dayDate.setDate(viewStartDate.getDate() + dayIndex);
              
              // Format the day header
              const formattedDate = format(dayDate, 'EEEE, MMMM d');
              const todayDate = isToday(dayDate);
              
              // Get resources that are available on this day
              const availableResources = mobileResources.filter(resource => {
                // Check if the resource has availability for this day
                const dayOfWeek = dayDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
                const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                const dayName = dayNames[dayOfWeek];
                
                // Find the availability record and parse working hours
                const availabilityRecord = mobileAvailability.find(ma => ma.resource_id === resource.id);
                if (!availabilityRecord || !availabilityRecord.working_hours) return false;
                
                // Parse the working hours JSON
                let workingHours;
                try {
                  workingHours = typeof availabilityRecord.working_hours === 'string'
                    ? JSON.parse(availabilityRecord.working_hours)
                    : availabilityRecord.working_hours;
                  
                  // Check if this day is enabled and has shifts
                  const dayConfig = workingHours[dayName];
                  return dayConfig?.enabled && dayConfig.shifts && dayConfig.shifts.length > 0;
                } catch (error) {
                  console.error('Error parsing working hours:', error);
                  return false;
                }
              });
              
              // Get all bookings for this day
              const dayBookings = events.filter(event => {
                const eventDate = new Date(event.start);
                return (
                  eventDate.getDate() === dayDate.getDate() &&
                  eventDate.getMonth() === dayDate.getMonth() &&
                  eventDate.getFullYear() === dayDate.getFullYear()
                );
              });

              // Skip days with no resources available - modern empty state
              if (availableResources.length === 0) {
                return (
                  <div 
                    key={dayIndex} 
                    className="bg-white/90 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-lg overflow-hidden"
                    style={{
                      backdropFilter: 'blur(20px) saturate(180%)',
                      WebkitBackdropFilter: 'blur(20px) saturate(180%)'
                    }}
                  >
                    {/* Modern day header - Mobile optimized */}
                    <div className={`px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-200/50 ${
                      todayDate ? 'bg-gradient-to-r from-purple-50 to-indigo-50' : 'bg-gradient-to-r from-gray-50 to-white'
                    }`}>
                      <h3 className={`text-base font-semibold ${todayDate ? 'text-purple-900' : 'text-gray-900'} flex items-center`}>
                        {format(dayDate, 'EEE, MMM d')}
                        {todayDate && <span className="ml-3 text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-medium">Today</span>}
                      </h3>
                    </div>
                    
                    <div className="flex flex-col items-center justify-center p-4 sm:p-6 bg-gradient-to-br from-gray-50/50 to-white">
                      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center mb-3">
                        <CalendarIcon className="h-6 w-6 text-gray-400" />
                      </div>
                      <p className="text-gray-500 text-sm text-center font-medium">No resources available</p>
                    </div>
                  </div>
                );
              }
              
              return (
                <div 
                  key={dayIndex} 
                  className="bg-white/90 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-lg overflow-hidden"
                  style={{
                    backdropFilter: 'blur(20px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(20px) saturate(180%)'
                  }}
                >
                                      {/* Modern day header - Mobile optimized */}
                    <div className={`px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-200/50 ${
                      todayDate ? 'bg-gradient-to-r from-purple-50 to-indigo-50' : 'bg-gradient-to-r from-gray-50 to-white'
                    }`}>
                      <h3 className={`text-base font-semibold ${todayDate ? 'text-purple-900' : 'text-gray-900'} flex items-center`}>
                        {format(dayDate, 'EEE, MMM d')}
                        {todayDate && <span className="ml-3 text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-medium">Today</span>}
                      </h3>
                    </div>
                  
                  {/* Modern resources section - Optimized for mobile width */}
                  <div className="p-2 sm:p-4">
                    <div className="space-y-2 sm:space-y-3">
                      {availableResources.map(resource => {
                        // Get working hours for this resource and day
                        const dayOfWeek = dayDate.getDay();
                        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                        const dayName = dayNames[dayOfWeek];
                        
                        // Find the availability record and parse working hours
                        const availabilityRecord = mobileAvailability.find(ma => ma.resource_id === resource.id);
                        if (!availabilityRecord || !availabilityRecord.working_hours) return null;
                        
                        let workingHours;
                        let startTime;
                        let endTime;
                        let timeSlots = [];
                        let totalMinutes = 0;
                        let startMinutes = 0;
                        let endMinutes = 0;
                        let resourceBookings = [];
                        
                        try {
                          workingHours = typeof availabilityRecord.working_hours === 'string'
                            ? JSON.parse(availabilityRecord.working_hours)
                            : availabilityRecord.working_hours;
                            
                          const dayConfig = workingHours[dayName];
                          if (!dayConfig?.enabled || !dayConfig.shifts || dayConfig.shifts.length === 0) return null;
                          
                          // Use the first shift for now
                          startTime = dayConfig.shifts[0].start;
                          endTime = dayConfig.shifts[0].end;
                          
                          // Calculate minutes from midnight for start and end time
                          const [startHours, startMins] = startTime.split(':').map(Number);
                          const [endHours, endMins] = endTime.split(':').map(Number);
                          
                          startMinutes = startHours * 60 + startMins;
                          endMinutes = endHours * 60 + endMins;
                          totalMinutes = endMinutes - startMinutes;
                          
                          // Create time slots every 30 minutes with a label for each slot
                          for (let i = 0; i <= totalMinutes; i += 30) {
                            const minutes = startMinutes + i;
                            const hours = Math.floor(minutes / 60);
                            const mins = minutes % 60;
                            
                            // Format time for display
                            const formattedHour = hours % 12 || 12; // Convert to 12-hour format
                            const ampm = hours < 12 ? 'am' : 'pm';
                            const timeLabel = `${formattedHour}${mins === 0 ? '' : ':30'}${ampm}`;
                            
                            timeSlots.push({
                              time: `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`,
                              left: (i / totalMinutes) * 100,
                              label: timeLabel
                            });
                          }
                          
                          // Get bookings for this resource and day
                          resourceBookings = dayBookings.filter(event => 
                            event.extendedProps.resource_id === resource.id
                          );
                        } catch (error) {
                          console.error('Error parsing resource configuration:', error);
                          return null;
                        }
                        
                        const hasBookings = resourceBookings.length > 0;
                        
                        return (
                          <div key={resource.id} className="bg-gradient-to-r from-gray-50/80 to-white/90 backdrop-blur-sm rounded-xl border border-gray-200/50 p-3 shadow-sm">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center">
                                  <span className="text-xs font-semibold text-purple-700">
                                    {resource.title.split(' ').map((name: string) => name[0]).join('')}
                                  </span>
                                </div>
                                <h4 className="font-semibold text-sm text-gray-900">{resource.title}</h4>
                              </div>
                              
                              <div className="flex items-center space-x-1 bg-white/60 rounded-lg px-2 py-1">
                                <Clock className="h-3 w-3 text-gray-500" />
                                <span className="text-xs font-medium text-gray-700">{formatTime(startTime).replace(":00", "")} - {formatTime(endTime).replace(":00", "")}</span>
                              </div>
                            </div>
                            
                            <div className="relative h-20 pt-6 bg-gradient-to-r from-white to-gray-50/50 rounded-xl overflow-hidden border border-gray-200/30 shadow-sm">
                              {/* Interactive Time Slots */}
                              {timeSlots.map((slot, i) => {
                                const nextSlot = timeSlots[i + 1];
                                const slotWidth = nextSlot ? nextSlot.left - slot.left : 100 - slot.left;
                                
                                // Calculate available time for this slot
                                const calculateAvailableTime = () => {
                                  const slotStartTime = slot.time;
                                  
                                  // Parse times to minutes from midnight
                                  const [startHours, startMins] = slotStartTime.split(':').map(Number);
                                  const slotStartMinutes = startHours * 60 + startMins;
                                  
                                  // Find the next booking that starts after this slot
                                  const nextBookings = resourceBookings
                                    .filter(booking => {
                                      const bookingStart = new Date(booking.start);
                                      const bookingStartMinutes = bookingStart.getHours() * 60 + bookingStart.getMinutes();
                                      return bookingStartMinutes > slotStartMinutes;
                                    })
                                    .sort((a, b) => {
                                      const aStart = new Date(a.start);
                                      const bStart = new Date(b.start);
                                      return aStart.getTime() - bStart.getTime();
                                    });
                                  
                                  // Calculate end time (either next booking start or end of day)
                                  const endTimeMinutes = endTime.split(':').map(Number);
                                  const dayEndMinutes = endTimeMinutes[0] * 60 + endTimeMinutes[1];
                                  
                                  let availableEndMinutes = dayEndMinutes;
                                  
                                  if (nextBookings.length > 0) {
                                    const nextBookingStart = new Date(nextBookings[0].start);
                                    const nextBookingStartMinutes = nextBookingStart.getHours() * 60 + nextBookingStart.getMinutes();
                                    availableEndMinutes = nextBookingStartMinutes;
                                  }
                                  
                                  const availableMinutes = availableEndMinutes - slotStartMinutes;
                                  return Math.max(0, availableMinutes);
                                };
                                
                                // Check if this slot is blocked by any booking or buffer
                                const isSlotBlocked = () => {
                                  const slotStartTime = slot.time;
                                  const nextSlot = timeSlots[i + 1];
                                  const slotEndTime = nextSlot ? nextSlot.time : endTime;
                                  
                                  // Parse times to minutes from midnight
                                  const [startHours, startMins] = slotStartTime.split(':').map(Number);
                                  const [endHours, endMins] = slotEndTime.split(':').map(Number);
                                  
                                  const slotStartMinutes = startHours * 60 + startMins;
                                  const slotEndMinutes = endHours * 60 + endMins;
                                  
                                  // Check if any booking overlaps with this slot
                                  return resourceBookings.some(booking => {
                                    const bookingStart = new Date(booking.start);
                                    const bookingEnd = new Date(booking.end);
                                    
                                    const bookingStartMinutes = bookingStart.getHours() * 60 + bookingStart.getMinutes();
                                    const bookingEndMinutes = bookingEnd.getHours() * 60 + bookingEnd.getMinutes();
                                    
                                    // Check for overlap
                                    return !(bookingEndMinutes <= slotStartMinutes || bookingStartMinutes >= slotEndMinutes);
                                  });
                                };
                                
                                const availableMinutes = calculateAvailableTime();
                                const availableHours = availableMinutes / 60;
                                const isBlocked = isSlotBlocked();
                                
                                // Format the available time display
                                const formatAvailableTime = (hours: number) => {
                                  if (hours === 0) return '0 hrs';
                                  if (hours === 1) return '1 hr';
                                  if (hours % 1 === 0) return `${hours} hrs`;
                                  const wholeHours = Math.floor(hours);
                                  const minutes = Math.round((hours - wholeHours) * 60);
                                  if (wholeHours === 0) return `${minutes} min`;
                                  return `${wholeHours}.${minutes.toString().padStart(2, '0')} hrs`;
                                };
                                
                                return (
                                  <div
                                    key={`slot-${i}`}
                                    className={`absolute top-0 h-full transition-all duration-200 ease-out ${
                                      isBlocked 
                                        ? 'cursor-default' 
                                        : 'hover:bg-purple-100/50 hover:border-l-2 hover:border-r-2 hover:border-purple-300 cursor-pointer group'
                                    }`}
                                    style={{
                                      left: `${slot.left}%`,
                                      width: `${slotWidth}%`,
                                      zIndex: 1
                                    }}
                                    title={`${slot.label} - ${nextSlot ? nextSlot.label : 'End'}`}
                                    onClick={() => {
                                      if (!isBlocked) {
                                        handleQuickBooking(resource, slot.time, availableHours, dayDate);
                                      }
                                    }}
                                  >
                                    {/* Available time tooltip - only show if not blocked */}
                                    {!isBlocked && (
                                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                                        <div className="bg-purple-600 text-white text-xs font-semibold px-2 py-1 rounded-lg shadow-lg">
                                          {formatAvailableTime(availableHours)}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                              
                              {/* Time indicators */}
                              {timeSlots.map((slot, i) => (
                                <React.Fragment key={i}>
                                  <div 
                                    className={`absolute h-full w-px ${
                                      slot.time.endsWith('00') ? 'bg-black' : 'bg-gray-200'
                                    }`}
                                    style={{ left: `${slot.left}%`, zIndex: 2 }}
                                  />
                                  {/* Show fewer labels on mobile: only display label for every even slot */}
                                  {(i % 2 === 0 || slot.time.endsWith('00')) && (
                                    <div 
                                      className="absolute top-1 text-[9px] text-gray-500 font-medium"
                                      style={{ 
                                        left: `${slot.left}%`, 
                                        transform: 'translateX(-50%)',
                                        whiteSpace: 'nowrap',
                                        zIndex: 3
                                      }}
                                    >
                                      {slot.label}
                                    </div>
                                  )}
                                </React.Fragment>
                              ))}
                              
                              {/* Add custom style specifically for buffer blocks */}
                              <style>
                                {`
                                  /* Ensure buffer blocks end exactly at time markers */
                                  .buffer-block {
                                    /* Use hard right edge instead of padding/margin */
                                    border-right: 1px solid #000000 !important;
                                    /* Prevent border from affecting width calculation */
                                    box-sizing: border-box !important;
                                  }
                                  
                                  /* Responsive adjustments for different screen sizes */
                                  @media (max-width: 480px) {
                                    .buffer-block {
                                      /* Ensure visibility at very small screens */
                                      min-width: 10px !important;
                                    }
                                  }
                                  
                                  @media (min-width: 481px) and (max-width: 768px) {
                                    .buffer-block {
                                      min-width: 20px !important;
                                    }
                                  }
                                `}
                              </style>
                              
                              {/* Bookings */}
                              {resourceBookings.map((booking, i) => {
                                const bookingStart = new Date(booking.start);
                                const bookingEnd = new Date(booking.end);
                                
                                // Calculate booking position
                                const bookingStartMinutes = bookingStart.getHours() * 60 + bookingStart.getMinutes();
                                let bookingEndMinutes = bookingEnd.getHours() * 60 + bookingEnd.getMinutes();
                                
                                // Check if this is a buffer block
                                const isBufferBlock = booking.extendedProps?.is_buffer_block === true;
                                
                                // For buffer blocks, ensure precise time calculations
                                if (isBufferBlock) {
                                  // Get the buffer time in minutes and log for debugging
                                  const bufferTimeMinutes = parseInt(booking.extendedProps.buffer_time);
                                  
                                  console.log(`Buffer block in view: ${booking.id}`);
                                  console.log(`  Start: ${bookingStart}`);
                                  console.log(`  End: ${bookingEnd}`);
                                  console.log(`  Buffer time: ${bufferTimeMinutes} minutes`);
                                  console.log(`  StartMinutes: ${bookingStartMinutes}`);
                                  console.log(`  EndMinutes: ${bookingEndMinutes}`);
                                  
                                  // For buffer blocks, calculate the exact end time based on buffer_time
                                  // This ensures the width is exactly based on the buffer duration
                                  const exactEndMinutes = bookingStartMinutes + bufferTimeMinutes;
                                  console.log(`  Recalculated end minutes: ${exactEndMinutes}`);
                                  
                                  // Recalculate the end percent based on the exact buffer time
                                  bookingEndMinutes = exactEndMinutes;
                                  
                                  // No need for tiny adjustments here - we'll handle precision in the rendering
                                }
                                
                                // Calculate percentage within the timeline
                                let bookingStartPercent = ((bookingStartMinutes - startMinutes) / totalMinutes) * 100;
                                let bookingEndPercent = ((bookingEndMinutes - startMinutes) / totalMinutes) * 100;
                                
                                // Clamp to visible area
                                bookingStartPercent = Math.max(0, Math.min(100, bookingStartPercent));
                                bookingEndPercent = Math.max(0, Math.min(100, bookingEndPercent));
                                
                                // Calculate booking width
                                let bookingWidth = bookingEndPercent - bookingStartPercent;
                                
                                // For buffer blocks, ensure exact width calculation
                                if (isBufferBlock) {
                                  // Instead of a fixed adjustment, use a precise calculation based on buffer time
                                  // This ensures the buffer block has the exact width regardless of screen size
                                  const bufferTimeMinutes = parseInt(booking.extendedProps.buffer_time);
                                  const preciseWidth = (bufferTimeMinutes / totalMinutes) * 100;
                                  
                                  // Use the most precise width calculation available
                                  bookingWidth = preciseWidth;
                                  console.log(`  Precise buffer width: ${bookingWidth}%`);
                                }
                                
                                // Apply minimum width (important for visibility on small screens)
                                bookingWidth = Math.max(bookingWidth, isBufferBlock ? 3 : 25);
                                
                                // Skip rendering if booking is not visible
                                if (bookingWidth <= 0) return null;
                                
                                // Create hover effect styles
                                const hoverStyles = `
                                  .booking-block-${booking.id}:hover {
                                    transform: translateY(-2px);
                                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
                                  }
                                `;
                                
                                return (
                                  <React.Fragment key={booking.id}>
                                    <style>{hoverStyles}</style>
                                    <div
                                      className={`booking-block-${booking.id} absolute h-12 top-8 rounded-lg overflow-hidden transition-all duration-200 cursor-pointer flex items-center shadow-sm ${isBufferBlock ? 'buffer-block' : ''}`}
                                      style={{
                                        left: `${bookingStartPercent}%`,
                                        width: `${bookingWidth}%`,
                                        background: isBufferBlock ? 
                                          'linear-gradient(135deg, #000000 0%, #1f1f1f 100%)' : 
                                          `linear-gradient(135deg, ${booking.backgroundColor} 0%, ${booking.backgroundColor} 100%)`,
                                        color: 'white',
                                        borderLeft: '3px solid',
                                        borderLeftColor: isBufferBlock ? '#000000' : (booking.secondaryColor || booking.backgroundColor),
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                                        minWidth: isBufferBlock ? '30px' : '60px', // Adjust minimum width based on type
                                        zIndex: 5, // Add z-index to ensure they appear above other elements
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        justifyContent: 'center',
                                        overflow: 'hidden',
                                        boxSizing: 'border-box' // Ensure border is included in width calculations
                                      }}
                                      onClick={() => handleBookingClick(booking)}
                                    >
                                      <div className="px-2 py-1 truncate text-xs font-medium w-full">
                                        {bookingWidth > 10 ? (isBufferBlock ? 'Buffer' : (
                                          <>
                                            #{booking.extendedProps.booking_number} - {booking.title}
                                            {bookingWidth > 30 && (
                                              <div className="text-[9px] font-normal text-white truncate mt-0.5">
                                                {getServiceTitle(booking)} - {booking.extendedProps.customer}
                                              </div>
                                            )}
                                          </>
                                        )) : ''}
                                        {bookingWidth > 30 && isBufferBlock && (
                                          <div className="text-[9px] font-normal text-white truncate mt-0.5">
                                            {booking.extendedProps.buffer_time} min buffer
                                          </div>
                                        )}
                                        
                                        {/* Payment Status Indicator for Week View */}
                                        {!isBufferBlock && bookingWidth > 20 && (() => {
                                          const totalAmount = booking.extendedProps.total_amount || 0;
                                          const payments = booking.extendedProps.payments || [];
                                          const totalPaid = payments
                                            .filter((p: any) => p.status === 'completed')
                                            .reduce((sum: number, p: any) => sum + p.amount, 0);

                                          let paymentStatus = '';
                                          
                                          if (totalAmount > 0) {
                                            if (totalPaid === 0) {
                                              paymentStatus = 'UNPAID';
                                            } else if (totalPaid >= totalAmount) {
                                              paymentStatus = 'PAID';
                                            } else {
                                              paymentStatus = 'PARTIAL';
                                            }
                                          }
                                          
                                          return paymentStatus ? (
                                            <div className="absolute top-1 right-1 px-1 py-0.5 text-[8px] font-bold rounded bg-black/40 text-white">
                                              {paymentStatus}
                                            </div>
                                          ) : null;
                                        })()}
                                        
                                        {/* Star Rating for Week View - Right side, left of paid badge */}
                                        {!isBufferBlock && bookingWidth > 25 && booking.extendedProps.status === 'completed' && (() => {
                                          const feedback = bookingFeedback.get(booking.id);
                                          const totalAmount = booking.extendedProps.total_amount || 0;
                                          const payments = booking.extendedProps.payments || [];
                                          const totalPaid = payments
                                            .filter((p: any) => p.status === 'completed')
                                            .reduce((sum: number, p: any) => sum + p.amount, 0);
                                          const hasPaymentStatus = totalAmount > 0;
                                          
                                          return feedback ? (
                                            <div className={`absolute top-1 ${hasPaymentStatus ? 'right-8' : 'right-1'} flex items-center bg-black/40 rounded px-1 py-0.5`}>
                                              <Star 
                                                size={10} 
                                                className="text-yellow-400 fill-current mr-0.5" 
                                              />
                                              <span className="text-white text-[8px] font-bold">
                                                {feedback.rating.toFixed(1)}
                                              </span>
                                            </div>
                                          ) : null;
                                        })()}
                                      </div>
                                    </div>
                                  </React.Fragment>
                                );
                              })}
                              
                              {/* Empty state when no bookings */}
                              {!hasBookings && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <span className="text-xs text-gray-500 font-medium bg-white/80 backdrop-blur-sm px-2 py-1 rounded-lg">No bookings scheduled</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Add custom styles for mobile optimization */}
      <style jsx>{`
        @media (max-width: 640px) {
          /* Optimize touch targets */
          button {
            min-height: 36px;
            min-width: 36px;
          }
          
          /* Better scrolling on iOS */
          main {
            -webkit-overflow-scrolling: touch;
          }
          
          /* Ensure resource containers have enough space for touches */
          .resource-container {
            padding-bottom: 8px;
          }
        }
      `}</style>
      
      {/* Booking info modal */}
      {isBookingModalOpen && selectedBooking && (
        <BookingInfoModal
          isOpen={isBookingModalOpen}
          booking={selectedBooking}
          onClose={() => {
            console.log('📱 User manually closed modal');
            setIsBookingModalOpen(false);
            setSelectedBooking(null);
          }}
          onUpdate={() => {
            fetchEvents(); // Refresh the events but keep the modal open
          }}
          onRescheduleMobileBooking={handleRescheduleMobileBooking}
        />
      )}

      {/* Buffer Block Modal */}
      {isBufferModalOpen && selectedBufferBlock && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      Buffer Block Details
                    </h3>
                    
                    <div className="mt-2 mb-4">
                      <p className="text-sm text-gray-500 mb-1">
                        <span className="font-medium">Duration:</span> {selectedBufferBlock.buffer_time} minutes
                      </p>
                      <p className="text-sm text-gray-500 mb-1">
                        <span className="font-medium">Start Time:</span> {selectedBufferBlock.start ? format(selectedBufferBlock.start, 'h:mm a') : 'N/A'}
                      </p>
                      <p className="text-sm text-gray-500 mb-1">
                        <span className="font-medium">End Time:</span> {selectedBufferBlock.end ? format(selectedBufferBlock.end, 'h:mm a') : 'N/A'}
                      </p>
                    </div>
                    
                    <div className="bg-gray-50 -ml-4 -mr-4 px-4 py-3 rounded-b-lg">
                      <div className="text-sm text-gray-500 mb-3">
                        Do you want to delete this buffer block?
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleDeleteBufferBlock}
                >
                  Delete Buffer
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setIsBufferModalOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Google Maps Modal */}
      {isGoogleMapsModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center">
            {/* Modern backdrop with blur */}
            <div 
              className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" 
              style={{
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)'
              }}
              onClick={() => setIsGoogleMapsModalOpen(false)}
            ></div>

            {/* Modern modal container */}
            <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 overflow-hidden transform transition-all duration-300 ease-out animate-in zoom-in-95 fade-in-0 w-full max-w-md mx-4" style={{
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)'
            }}>
              {/* Header section */}
              <div className="px-6 pt-6 pb-4">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center">
                      <MapPin className="h-6 w-6 text-purple-600" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      Open in Google Maps
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Do you want to open this address in Google Maps?
                    </p>
                  </div>
                </div>
              </div>

              {/* Address section */}
              <div className="px-6 pb-6">
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-4 border border-gray-200/50">
                  <div className="flex items-start space-x-3">
                    <MapPin className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm font-medium text-gray-800 leading-relaxed">
                      {selectedBookingAddress}
                    </p>
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="bg-gradient-to-br from-gray-50/80 to-white/80 backdrop-blur-sm px-6 py-4 border-t border-gray-200/50 flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3" style={{
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)'
              }}>
                <button
                  type="button"
                  className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl hover:bg-white/90 focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
                  onClick={() => setIsGoogleMapsModalOpen(false)}
                  style={{ minHeight: '44px' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-2.5 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
                  onClick={() => {
                    openGoogleMaps(selectedBookingAddress);
                    setIsGoogleMapsModalOpen(false);
                  }}
                  style={{ minHeight: '44px' }}
                >
                  Map
                </button>
                <button
                  type="button"
                  className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-2.5 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 rounded-2xl focus:outline-none focus:ring-2 focus:ring-green-500/20 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleMapAndNotify}
                  disabled={isSendingSMS}
                  style={{ minHeight: '44px' }}
                >
                  {isSendingSMS ? 'Sending...' : 'Map and Notify'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Booking Reschedule Modal */}
      <CreateMobileBookingModal
        isOpen={isRescheduleModalOpen}
        onClose={() => {
          setIsRescheduleModalOpen(false);
          setRescheduleData(null);
        }}
        onSuccess={() => {
          setIsRescheduleModalOpen(false);
          setRescheduleData(null);
          // Refresh the calendar
          fetchEvents();
        }}
        isReschedule={true}
        existingBookingId={selectedBooking?.id}
        prefilledData={rescheduleData}
      />

      {/* Quick Booking Modal */}
      <CreateMobileBookingModal
        isOpen={isQuickBookingModalOpen}
        onClose={() => {
          setIsQuickBookingModalOpen(false);
          setQuickBookingData(null);
        }}
        onSuccess={() => {
          setIsQuickBookingModalOpen(false);
          setQuickBookingData(null);
          // Refresh the calendar
          fetchEvents();
        }}
        prefilledData={quickBookingData}
      />
    </div>
  );
} 